"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { FiChevronDown, FiFileText } from "react-icons/fi";
import Button from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { MembershipShopBillingStart } from "@/types/enums/membership";
import { MembershipCyclePrice, MembershipShopCartLine, ShopItem } from "@/types/shop";
import { lineFromMembership, membershipFrequencyLabel, useMembershipBillingTerms } from "./utils";
interface MembershipConfiguratorDialogProps {
  item: ShopItem | null;
  onConfirm: (line: MembershipShopCartLine) => void;
  onCancel: () => void;
}
const billingStartValue = (billingStart: MembershipShopBillingStart | null | undefined): string => {
  if (billingStart === MembershipShopBillingStart.FIRST_OF_MONTH) {
    return "The 1st of each month";
  }
  if (billingStart === MembershipShopBillingStart.FIFTEENTH_OF_MONTH) {
    return "The 15th of each month";
  }
  return "Right after checkout";
};
const splitFirstCycleBand = (cycleBands: MembershipCyclePrice[]): {
  firstCycleBand: MembershipCyclePrice | null;
  laterCycleBands: MembershipCyclePrice[];
} => {
  const firstBand = cycleBands[0];
  if (!firstBand) return {
    firstCycleBand: null,
    laterCycleBands: []
  };
  const includesLaterCycles = firstBand.fromCycle === 1 && (firstBand.toCycle === null || firstBand.toCycle > 1);
  return {
    firstCycleBand: {
      ...firstBand,
      fromCycle: 1,
      toCycle: 1
    },
    laterCycleBands: [...(includesLaterCycles ? [{
      ...firstBand,
      fromCycle: 2
    }] : []), ...cycleBands.slice(1)]
  };
};
const cycleBandLabel = ({
  fromCycle,
  toCycle
}: MembershipCyclePrice): string => {
  if (toCycle === null) return `Cycle ${fromCycle} onward`;
  if (toCycle === fromCycle) return `Cycle ${fromCycle}`;
  return `Cycles ${fromCycle}\u2013${toCycle}`;
};
const DetailRow: React.FC<{
  label: string;
  value: string;
  strong?: boolean;
  compact?: boolean;
}> = t0 => {
  const $ = _c(16);
  const {
    label,
    value,
    strong: t1,
    compact: t2
  } = t0;
  const strong = t1 === undefined ? false : t1;
  const compact = t2 === undefined ? false : t2;
  const t3 = compact ? "py-1.5" : "py-3";
  let t4;
  if ($[0] !== t3) {
    t4 = cx("flex items-baseline justify-between gap-4", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = strong ? "font-medium text-foreground" : "text-foreground-secondary";
  let t6;
  if ($[2] !== t5) {
    t6 = cx("text-caption", t5);
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== label || $[5] !== t6) {
    t7 = <dt className={t6}>{label}</dt>;
    $[4] = label;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = strong ? "text-body-strong" : "text-body-emphasis";
  let t9;
  if ($[7] !== t8) {
    t9 = cx("text-right text-foreground tabular-nums", t8);
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t9 || $[10] !== value) {
    t10 = <dd className={t9}>{value}</dd>;
    $[9] = t9;
    $[10] = value;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t4 || $[14] !== t7) {
    t11 = <div className={t4}>{t7}{t10}</div>;
    $[12] = t10;
    $[13] = t4;
    $[14] = t7;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  return t11;
};
const MembershipConfiguratorDialog: React.FC<MembershipConfiguratorDialogProps> = t0 => {
  const $ = _c(33);
  const {
    item,
    onConfirm,
    onCancel
  } = t0;
  const [cyclePricingOpen, setCyclePricingOpen] = React.useState(false);
  const {
    effectivelyRenews,
    hasFutureBillingStart,
    chargeLabel
  } = useMembershipBillingTerms(item);
  const initialPrice = item?.displayPrice ?? item?.price ?? 0;
  const promotionalSavings = item ? Math.max(0, item.price - initialPrice) : 0;
  const setupFee = typeof item?.setupFee === "number" && item.setupFee !== 0 ? item.setupFee : null;
  let renewal;
  let t1;
  if ($[0] !== effectivelyRenews || $[1] !== item) {
    renewal = effectivelyRenews && typeof item?.recurringPrice === "number" && item?.billingFrequency ? {
      price: item.recurringPrice,
      frequency: item.billingFrequency
    } : null;
    t1 = renewal ? `${formatCurrency(renewal.price)} ${membershipFrequencyLabel(renewal.frequency)}` : effectivelyRenews ? null : "Does not auto-renew";
    $[0] = effectivelyRenews;
    $[1] = item;
    $[2] = renewal;
    $[3] = t1;
  } else {
    renewal = $[2];
    t1 = $[3];
  }
  const renewalValue = t1;
  let T0;
  let T1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[4] !== chargeLabel || $[5] !== cyclePricingOpen || $[6] !== effectivelyRenews || $[7] !== hasFutureBillingStart || $[8] !== initialPrice || $[9] !== item || $[10] !== onCancel || $[11] !== onConfirm || $[12] !== promotionalSavings || $[13] !== renewal || $[14] !== renewalValue || $[15] !== setupFee) {
    const cycleBands = item?.membershipCyclePricing ?? [];
    const {
      firstCycleBand,
      laterCycleBands: splitLaterCycleBands
    } = splitFirstCycleBand(cycleBands);
    const laterCycleBands = effectivelyRenews ? splitLaterCycleBands : [];
    const finalCycle = effectivelyRenews ? cycleBands[cycleBands.length - 1]?.toCycle ?? null : null;
    T1 = Dialog;
    t4 = item !== null;
    if ($[22] !== onCancel) {
      t5 = open => {
        if (open) {
          return;
        }
        setCyclePricingOpen(false);
        onCancel();
      };
      $[22] = onCancel;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    T0 = DialogContent;
    t2 = "max-w-xl gap-0 overflow-hidden p-0";
    t3 = item && <><div className="min-h-0 flex-1 overflow-y-auto px-6 py-7 md:px-8 md:py-8"><header className="grid gap-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-start"><div className="min-w-0"><p className="text-caption font-medium text-primary">{item.category ?? "Membership"}</p><DialogTitle className="mt-2 text-h1 font-serif font-normal leading-tight text-foreground">{item.name}</DialogTitle><DialogDescription className="mt-3 max-w-prose text-body leading-relaxed text-foreground-secondary">{hasFutureBillingStart ? "Save a card now \u2014 you won't be charged until billing begins." : "Your membership starts as soon as checkout is complete."}</DialogDescription></div><div className="md:min-w-40 md:text-right"><p className="text-caption text-foreground-secondary">{chargeLabel}</p><div className="mt-2 flex flex-wrap items-baseline gap-2 md:justify-end"><p className="font-serif text-[2rem] font-medium leading-none tracking-tight text-foreground tabular-nums md:text-[2.25rem]"><span className="sr-only">Enrollment charge: </span>{formatCurrency(initialPrice)}</p>{promotionalSavings > 0 && <p className="text-caption text-foreground-secondary line-through tabular-nums"><span className="sr-only">Original price: </span>{formatCurrency(item.price)}</p>}</div>{promotionalSavings > 0 && <p className="mt-2 text-body-emphasis text-primary"><span className="sr-only">You </span>save {formatCurrency(promotionalSavings)}</p>}</div></header>{item.description && <section className="mt-7 border-t border-border pt-5"><p className="text-caption font-medium text-foreground">What's included</p><p className="mt-2 whitespace-pre-line text-body leading-relaxed text-foreground-secondary">{item.description}</p></section>}<section className="mt-7 border-t border-border"><dl className="divide-y divide-border">{setupFee !== null && <DetailRow label="Includes one-time setup fee" value={formatCurrency(setupFee)} />}{renewalValue && <DetailRow label="Renews" value={renewalValue} />}<DetailRow label="Billing starts" value={billingStartValue(item.shopBillingStart)} /></dl>{firstCycleBand && <Collapsible open={cyclePricingOpen} onOpenChange={setCyclePricingOpen}><CollapsibleTrigger className="mt-4 flex cursor-pointer items-center gap-1.5 text-caption font-medium text-primary hover:underline"><FiChevronDown aria-hidden={true} className={cx("h-3.5 w-3.5 transition-transform", cyclePricingOpen && "rotate-180")} /><span>{cyclePricingOpen ? "Hide full cycle pricing" : "View full cycle pricing"}</span></CollapsibleTrigger><CollapsibleContent><p className="mt-4 text-caption font-medium text-foreground">Enrollment charge</p><dl className="mt-1 border-t border-border pt-1"><DetailRow label={cycleBandLabel(firstCycleBand)} value={formatCurrency(firstCycleBand.price)} compact={true} />{setupFee !== null && <DetailRow label="One-time setup fee" value={formatCurrency(setupFee)} compact={true} />}{promotionalSavings > 0 && <DetailRow label="Promotional discount" value={`-${formatCurrency(promotionalSavings)}`} compact={true} />}<div className="mt-1 border-t border-border"><DetailRow label={chargeLabel} value={formatCurrency(initialPrice)} strong={true} /></div></dl>{laterCycleBands.length > 0 && <><p className="mt-5 text-caption font-medium text-foreground">Later cycles</p><dl className="mt-1 divide-y divide-border border-t border-border">{laterCycleBands.map(_temp)}</dl></>}{finalCycle !== null && <p className="mt-3 text-caption leading-relaxed text-foreground-secondary">{`Billing ends after cycle ${finalCycle}.`}</p>}</CollapsibleContent></Collapsible>}{renewal && <p className="mt-4 text-caption leading-relaxed text-foreground-secondary">Future cycle prices may vary.</p>}</section>{item.form && <div className="mt-6 flex items-start gap-2 text-caption leading-relaxed text-foreground-secondary"><FiFileText aria-hidden={true} className="mt-0.5 h-4 w-4 flex-none text-primary" /><p>You'll complete {item.form.name} during checkout, before any payment is taken.</p></div>}</div><DialogFooter><Button variant="secondary" onClick={onCancel} className="cursor-pointer"><span>Cancel</span></Button><Button variant="primary" onClick={() => onConfirm(lineFromMembership({
          ...item,
          autoRenew: effectivelyRenews
        }))} className="cursor-pointer"><span>Add membership</span></Button></DialogFooter></>;
    $[4] = chargeLabel;
    $[5] = cyclePricingOpen;
    $[6] = effectivelyRenews;
    $[7] = hasFutureBillingStart;
    $[8] = initialPrice;
    $[9] = item;
    $[10] = onCancel;
    $[11] = onConfirm;
    $[12] = promotionalSavings;
    $[13] = renewal;
    $[14] = renewalValue;
    $[15] = setupFee;
    $[16] = T0;
    $[17] = T1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    T0 = $[16];
    T1 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
    t5 = $[21];
  }
  let t6;
  if ($[24] !== T0 || $[25] !== t2 || $[26] !== t3) {
    t6 = <T0 className={t2}>{t3}</T0>;
    $[24] = T0;
    $[25] = t2;
    $[26] = t3;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== T1 || $[29] !== t4 || $[30] !== t5 || $[31] !== t6) {
    t7 = <T1 open={t4} onOpenChange={t5}>{t6}</T1>;
    $[28] = T1;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  return t7;
};
export default MembershipConfiguratorDialog;
function _temp(band) {
  return <DetailRow key={band.fromCycle} label={cycleBandLabel(band)} value={formatCurrency(band.price)} />;
}